import {
  Directive,
  EmbeddedViewRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { io } from 'fp-ts';
import * as mobx from 'mobx';

/**
 * @example
 * ```
 * <div *mdReaction="store.toIO('foo') as foo">
 *   {{ foo }}
 * </div>
 * <div *mdReaction="store.toIO('foo', 'bar') as data">
 *   {{ data.foo }} <br />
 *   {{ data.bar }}
 * </div>
 * <div *mdReaction="store.toIO(['foo', 'bar']) as data">
 *   {{ data.foo }} <br />
 *   {{ data.bar }}
 * </div>
 * ```
 */
@Directive({ selector: '[mdReaction]' })
export class ReactionDirective<T = unknown> implements OnChanges, OnInit, OnDestroy {
  private readonly _context: MdReactionContext<T> = new MdReactionContext<T>();
  private view?: EmbeddedViewRef<any>;

  private readonly disposers: mobx.IReactionDisposer[] = [];

  @Input()
  mdReaction!: io.IO<T>;

  @Input()
  mdReactionOpts?: mobx.IReactionOptions<T, boolean>;

  /**
   * Mobx reaction wrapper that will run disposer on ngOnDestroy.
   */
  private readonly reaction = <T>(
    expression: (r: mobx.IReactionPublic) => T,
    effect: (arg: T, prev: T | undefined, r: mobx.IReactionPublic) => void,
    opts?: mobx.IReactionOptions<T, boolean>,
  ): mobx.IReactionDisposer => {
    const disposer = mobx.reaction(expression, effect, opts);

    this.disposers.push(disposer);

    return disposer;
  };

  private readonly disposeReactions = () => {
    this.disposers.forEach(x => typeof x === 'function' && x());
  };

  constructor(
    protected readonly templateRef: TemplateRef<MdReactionContext<T>>,
    protected readonly viewContainer: ViewContainerRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('mdReaction' in changes || 'mdReactionOpts' in changes) {
      this.disposeReactions();

      const value = mobx.runInAction(this.mdReaction);

      this._context.$implicit = value;
      this._context.mdReaction = value;

      if (!this.view) {
        this.view = this.viewContainer.createEmbeddedView(
          this.templateRef,
          this._context,
        );
      }

      this.reaction(
        this.mdReaction,
        data => {
          this._context.mdReaction = data;
          this._context.$implicit = data;

          this.view?.detectChanges();
        },
        {
          fireImmediately: true,
          equals: mobx.comparer.shallow,
          ...this.mdReactionOpts,
        },
      );
    }
  }

  ngOnInit() {}

  ngOnDestroy() {
    this.disposeReactions();
  }

  /**
   * Asserts the correct type of the context for the template that `ReactionDirective` will render.
   *
   * The presence of this method is a signal to the Ivy template type-check compiler that the
   * `ReactionDirective` structural directive renders its template with a specific context type.
   */
  static ngTemplateContextGuard<T>(
    _dir: ReactionDirective<T>,
    _ctx: unknown,
  ): _ctx is MdReactionContext<T> {
    return true;
  }
}

export class MdReactionContext<T = unknown> {
  public $implicit: T = null as unknown as T;
  public mdReaction: T = null as unknown as T;
}
